import * as React from "react"
import {useEffect} from "react"
import {Box, Grid, Typography, Paper, LinearProgress} from "@mui/material"
import {CardSeeker, IconSeeker} from "@ui-seeker/core"
import {BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer} from 'recharts'

import {pageTitle} from "@app/@signals"
import useUserStats from "./@hooks/useUserStats"


const StatCard = ({ title, value, icon, color, subtitle }) => (
	<CardSeeker sx={{ height: '100%' }}>
		<Box sx={{ p: 3 }}>
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
				<Box sx={{ 
					backgroundColor: `${color}20`, 
					borderRadius: 2, 
					p: 1.5,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center'
				}}>
					<IconSeeker icon={icon} sx={{ color, fontSize: 28 }} />
				</Box>
			</Box>
			<Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
				{value}
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
				{title}
			</Typography>
			{subtitle && (
				<Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
					{subtitle}
				</Typography>
			)}
		</Box>
	</CardSeeker>
)

const TierCard = ({ tierInfo }) => {
	if (!tierInfo) return null
	
	const { currentTier, nextTier, progressPercentage, mappingsToNextTier } = tierInfo
	
	return (
		<CardSeeker>
			<Box sx={{ p: 3 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
					<Box sx={{ 
						backgroundColor: '#8b5cf620', 
						borderRadius: 2, 
						p: 1.5,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						mr: 2
					}}>
						<IconSeeker icon="medal" sx={{ color: '#8b5cf6', fontSize: 28 }} />
					</Box>
					<Box>
						<Typography variant="h5" sx={{ fontWeight: 700, color: '#8b5cf6' }}>
							{currentTier.name}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Current Rate: ${currentTier.price} per mapping
						</Typography>
					</Box>
				</Box>

				{nextTier && (
					<Box>
						<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
							<Typography variant="body2" color="text.secondary">
								Progress to {nextTier.name}
							</Typography>
							<Typography variant="body2" sx={{ fontWeight: 600 }}>
								{mappingsToNextTier.toLocaleString()} mappings to go
							</Typography>
						</Box>
						<LinearProgress 
							variant="determinate" 
							value={progressPercentage} 
							sx={{ 
								height: 8, 
								borderRadius: 1,
								backgroundColor: '#e5e7eb',
								'& .MuiLinearProgress-bar': {
									backgroundColor: '#8b5cf6',
									borderRadius: 1
								}
							}}
						/>
						<Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
							Next tier rate: ${nextTier.price} per mapping
						</Typography>
					</Box>
				)}

				{!nextTier && (
					<Box sx={{ p: 2, backgroundColor: '#f3f4f6', borderRadius: 2 }}>
						<Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
							🎉 You've reached the highest tier!
						</Typography>
					</Box>
				)}
			</Box>
		</CardSeeker>
	)
}


export default function HomePage() {
	const { isLoading, stats } = useUserStats()

	// Home has no PageWrapper, so feed the shell header title directly.
	useEffect(() => {
		pageTitle.value = 'Home'
		return () => { pageTitle.value = '' }
	}, [])

	if (isLoading) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
				<IconSeeker icon="spinner" spin sx={{ fontSize: 48, color: 'primary.main' }} />
			</Box>
		)
	}

	return (
		<Box sx={{ p: 3 }}>
			<Box sx={{ mb: 4 }}>
				<Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
					Mapping Stats
				</Typography>
				<Typography variant="body1" color="text.secondary">
					Overview of your activity and mapping statistics
				</Typography>
			</Box>

			<Grid container spacing={3}>
				{/* Tier Card - Full Width - Only for external users */}
				{stats.isExternal && (
					<Grid item xs={12}>
						<TierCard tierInfo={stats.tierInfo} />
					</Grid>
				)}

				{/* Total Mappings */}
				<Grid item xs={12} sm={6} md={stats.isExternal ? 3 : 4}>
					<StatCard
						title="Total Mappings"
						value={stats.totalMappings.toLocaleString()}
						icon="check-circle"
						color="#10b981"
					/>
				</Grid>

				{/* Total Earnings - Only for external users */}
				{stats.isExternal && (
					<Grid item xs={12} sm={6} md={3}>
						<StatCard
							title="Total Earnings"
							value={`$${stats.totalEarnings.toFixed(2)}`}
							icon="dollar-sign"
							color="#3b82f6"
							subtitle={stats.tierInfo ? `Current rate: $${stats.tierInfo.currentTier.price}` : ''}
						/>
					</Grid>
				)}

				{/* Pending Mappings */}
				<Grid item xs={12} sm={6} md={stats.isExternal ? 3 : 4}>
					<StatCard
						title="Pending Mappings"
						value={stats.pendingMappings.toLocaleString()}
						icon="clock"
						color="#f59e0b"
					/>
				</Grid>

				{/* Average per day */}
				<Grid item xs={12} sm={6} md={stats.isExternal ? 3 : 4}>
					<StatCard
						title="Daily Average"
						value={stats.avgMappingsPerDay.toFixed(1)}
						icon="chart-line"
						color="#8b5cf6"
						subtitle="Last 7 days"
					/>
				</Grid>

				{/* Weekly Chart */}
				<Grid item xs={12}>
					<CardSeeker>
						<Box sx={{ p: 3 }}>
							<Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
								Last Week Mappings
							</Typography>
							<ResponsiveContainer width="100%" height={300}>
								<BarChart data={stats.weeklyData}>
									<CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
									<XAxis 
										dataKey="date" 
										tick={{ fill: '#6b7280', fontSize: 12 }}
										tickLine={{ stroke: '#e5e7eb' }}
									/>
									<YAxis 
										tick={{ fill: '#6b7280', fontSize: 12 }}
										tickLine={{ stroke: '#e5e7eb' }}
									/>
									<Tooltip 
										contentStyle={{ 
											backgroundColor: '#fff', 
											border: '1px solid #e5e7eb',
											borderRadius: 8,
											boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
										}}
										labelStyle={{ fontWeight: 600, marginBottom: 4 }}
									/>
									<Bar 
										dataKey="count" 
										fill="#3b82f6" 
										radius={[8, 8, 0, 0]}
										name="Mappings"
									/>
								</BarChart>
							</ResponsiveContainer>
						</Box>
					</CardSeeker>
				</Grid>

				{/* Additional Info Cards */}
				<Grid item xs={12} md={6}>
					<CardSeeker sx={{ height: '100%' }}>
						<Box sx={{ p: 3 }}>
							<Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
								<IconSeeker icon="trophy" sx={{ color: '#f59e0b', fontSize: 24, mr: 1.5 }} />
								<Typography variant="h6" sx={{ fontWeight: 600 }}>
									Performance
								</Typography>
							</Box>
							<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
								{stats.isExternal 
									? `Keep a steady pace to maximize your earnings. Your current average is `
									: `Great work! Your current average is `
								}
								<strong>{stats.avgMappingsPerDay.toFixed(1)} mappings per day</strong>.
							</Typography>
							{stats.isExternal && stats.totalMappings > 0 && stats.tierInfo && (
								<Box sx={{ mt: 2, p: 2, backgroundColor: '#f3f4f6', borderRadius: 2 }}>
									<Typography variant="caption" color="text.secondary">
										Monthly projection (30 days at current rate)
									</Typography>
									<Typography variant="h6" sx={{ fontWeight: 600, color: '#3b82f6' }}>
										~${(stats.avgMappingsPerDay * 30 * stats.tierInfo.currentTier.price).toFixed(2)}
									</Typography>
								</Box>
							)}
							{!stats.isExternal && stats.totalMappings > 0 && (
								<Box sx={{ mt: 2, p: 2, backgroundColor: '#f3f4f6', borderRadius: 2 }}>
									<Typography variant="caption" color="text.secondary">
										Monthly projection (30 days at current rate)
									</Typography>
									<Typography variant="h6" sx={{ fontWeight: 600, color: '#3b82f6' }}>
										~{(stats.avgMappingsPerDay * 30).toFixed(0)} mappings
									</Typography>
								</Box>
							)}
						</Box>
					</CardSeeker>
				</Grid>

				<Grid item xs={12} md={6}>
					<CardSeeker sx={{ height: '100%' }}>
						<Box sx={{ p: 3 }}>
							<Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
								<IconSeeker icon="lightbulb" sx={{ color: '#10b981', fontSize: 24, mr: 1.5 }} />
								<Typography variant="h6" sx={{ fontWeight: 600 }}>
									Tips
								</Typography>
							</Box>
							<Box component="ul" sx={{ pl: 2, m: 0 }}>
								<Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
									Review pending mappings regularly to maintain workflow
								</Typography>
								<Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
									Use filters to find products more quickly
								</Typography>
								<Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 2 }}>
									Verify the quality of your mappings to maintain accuracy
								</Typography>
							</Box>
							
							<Box sx={{ 
								mt: 2, 
								p: 2, 
								backgroundColor: '#fef3c7', 
								borderLeft: '4px solid #f59e0b',
								borderRadius: 1
							}}>
								<Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
									<IconSeeker icon="triangle-exclamation" sx={{ color: '#f59e0b', fontSize: 20, mr: 1, mt: 0.2 }} />
									<Box>
										<Typography variant="body2" sx={{ fontWeight: 600, color: '#92400e', mb: 0.5 }}>
											Quality Control
										</Typography>
										<Typography variant="caption" sx={{ color: '#78350f', display: 'block' }}>
											We have automated reliability measures in place. Some mappings are test cases with known results. 
											Low accuracy may result in termination of the collaboration.
										</Typography>
									</Box>
								</Box>
							</Box>
						</Box>
					</CardSeeker>
				</Grid>
			</Grid>
		</Box>
	)
}
